import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Badge,
  ErrorBanner,
  formatDate,
  type BadgeColor,
} from '../components/ui';

interface Document {
  id: string;
  filename: string;
  status: 'draft' | 'sent' | 'partially_signed' | 'completed' | 'voided' | 'declined';
  createdAt: string;
  sentAt?: string;
  completedAt?: string;
}

interface Signer {
  id: string;
  email: string;
  name?: string;
  status: 'pending' | 'signed' | 'declined';
  signedAt?: string;
}

interface AuditEntry {
  id: string;
  action: string;
  signerId?: string;
  ip?: string;
  occurredAt: string;
}

const statusColor: Record<Document['status'], BadgeColor> = {
  draft: 'gray',
  sent: 'blue',
  partially_signed: 'yellow',
  completed: 'green',
  voided: 'gray',
  declined: 'red',
};

export function EsignaturePage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['esign-docs'],
    queryFn: () => api.get<{ data: Document[] }>('/esignature/documents').catch(() => ({ data: [] })),
  });

  const docs = q.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="eSignature"
        subtitle={`${docs.length} documents`}
        action={<Button onClick={() => setShowCreate(true)}>+ New document</Button>}
      />

      {q.isLoading ? (
        <Spinner />
      ) : docs.length === 0 ? (
        <Card>
          <EmptyState
            title="No documents yet"
            description="Upload a PDF and send it for signature."
            action={<Button onClick={() => setShowCreate(true)}>+ New document</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {docs.map((d) => (
            <Card key={d.id} onClick={() => setSelectedId(d.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{d.filename}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                    Created {formatDate(d.createdAt)}
                    {d.sentAt && ` · Sent ${formatDate(d.sentAt)}`}
                  </div>
                </div>
                <Badge color={statusColor[d.status]}>{d.status.replace('_', ' ')}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateDocModal
          onClose={() => setShowCreate(false)}
          onSaved={(id) => {
            qc.invalidateQueries({ queryKey: ['esign-docs'] });
            setShowCreate(false);
            setSelectedId(id);
          }}
        />
      )}

      {selectedId && (
        <DocumentDetail
          docId={selectedId}
          onClose={() => setSelectedId(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['esign-docs'] })}
        />
      )}
    </div>
  );
}

function CreateDocModal({ onClose, onSaved }: { onClose: () => void; onSaved: (id: string) => void }) {
  const [filename, setFilename] = useState('');
  const [r2Key, setR2Key] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => api.post<{ id: string }>('/esignature/documents', { filename, r2Key }),
    onSuccess: (d) => onSaved(d.id),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  return (
    <Modal onClose={onClose} title="New document">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!filename) return setError('Filename required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Filename *">
          <Input value={filename} onChange={setFilename} placeholder="contract.pdf" />
        </Field>
        <Field label="R2 storage key" hint="Upload your PDF to R2 first, then paste the key here.">
          <Input value={r2Key} onChange={setR2Key} placeholder="esign/contract-123.pdf" />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DocumentDetail({
  docId,
  onClose,
  onSaved,
}: {
  docId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const docQ = useQuery({ queryKey: ['esign-doc', docId], queryFn: () => api.get<Document>(`/esignature/documents/${docId}`) });
  const signersQ = useQuery({
    queryKey: ['esign-signers', docId],
    queryFn: () => api.get<{ data: Signer[] }>(`/esignature/documents/${docId}/signers`).catch(() => ({ data: [] })),
  });
  const auditQ = useQuery({
    queryKey: ['esign-audit', docId],
    queryFn: () => api.get<{ data: AuditEntry[] }>(`/esignature/documents/${docId}/audit`).catch(() => ({ data: [] })),
  });

  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');

  const addSignerMut = useMutation({
    mutationFn: () => api.post(`/esignature/documents/${docId}/signers`, { email: newEmail, name: newName || undefined }),
    onSuccess: () => {
      setNewEmail('');
      setNewName('');
      qc.invalidateQueries({ queryKey: ['esign-signers', docId] });
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  const sendMut = useMutation({
    mutationFn: () => api.post(`/esignature/documents/${docId}/send`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['esign-doc', docId] });
      onSaved();
    },
  });

  const voidMut = useMutation({
    mutationFn: () => api.post(`/esignature/documents/${docId}/void`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['esign-doc', docId] });
      onSaved();
    },
  });

  if (docQ.isLoading) {
    return (
      <Modal onClose={onClose} title="Loading…">
        <Spinner />
      </Modal>
    );
  }

  const doc = docQ.data!;
  const signers = signersQ.data?.data ?? [];
  const audit = auditQ.data?.data ?? [];

  return (
    <Modal onClose={onClose} title={doc.filename} width={720}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Badge color={statusColor[doc.status]}>{doc.status.replace('_', ' ')}</Badge>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {doc.status === 'draft' && (
              <Button onClick={() => sendMut.mutate()} disabled={sendMut.isPending || signers.length === 0}>
                {sendMut.isPending ? 'Sending…' : 'Send for signature'}
              </Button>
            )}
            {doc.status !== 'voided' && doc.status !== 'completed' && (
              <Button variant="danger" onClick={() => voidMut.mutate()} disabled={voidMut.isPending}>
                Void
              </Button>
            )}
          </div>
        </div>

        <div>
          <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, margin: '0 0 var(--space-2)' }}>Signers</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', marginBottom: 'var(--space-2)' }}>
            {signers.map((s) => (
              <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-2)', background: 'var(--color-surface-secondary)', borderRadius: 'var(--radius-md)' }}>
                <div>
                  <div style={{ fontSize: 'var(--text-sm)' }}>{s.name ?? s.email}</div>
                  {s.name && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{s.email}</div>}
                </div>
                <Badge color={s.status === 'signed' ? 'green' : s.status === 'declined' ? 'red' : 'yellow'}>
                  {s.status}
                </Badge>
              </div>
            ))}
          </div>
          {doc.status === 'draft' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--space-2)' }}>
              <Input value={newName} onChange={setNewName} placeholder="Name" />
              <Input value={newEmail} onChange={setNewEmail} placeholder="Email" type="email" />
              <Button onClick={() => addSignerMut.mutate()} disabled={!newEmail || addSignerMut.isPending}>
                + Add
              </Button>
            </div>
          )}
        </div>

        <div>
          <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, margin: '0 0 var(--space-2)' }}>Audit trail</h4>
          {audit.length === 0 ? (
            <p style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>No events yet.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 'var(--text-sm)' }}>
              {audit.map((a) => (
                <li key={a.id} style={{ padding: 'var(--space-1) 0' }}>
                  <span style={{ color: 'var(--color-text-tertiary)' }}>{formatDate(a.occurredAt)}</span> · {a.action}
                  {a.ip && <span style={{ color: 'var(--color-text-tertiary)' }}> ({a.ip})</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <ErrorBanner error={error} />
      </div>
    </Modal>
  );
}
